/**
 * Whether this page is being torn down, so the automatic bug report can tell
 * a request the BROWSER cancelled from one that failed.
 *
 * A reload, a navigation to another document, or a closed tab cancels every
 * fetch still in flight. Each one rejects with a `TypeError` ("Failed to
 * fetch" in Chrome, "NetworkError when attempting to fetch resource." in
 * Firefox, "Load failed" in Safari) — the same error a real network failure
 * gives — and the old page's handlers still run long enough to file it. So a
 * refresh during a slow request filed a bug report against a server that had
 * answered 200 (staging `6e302280`: the compression request returned 37 ms
 * after the reload began).
 *
 * `pagehide` fires before those rejections (Chromium order: `beforeunload`,
 * `pagehide`, `visibilitychange`, then the rejected fetch), so a flag set
 * there is already true when the error arrives. `pageshow` with `persisted`
 * clears it: a page restored from the back/forward cache is live again.
 */

export interface UnloadWatch {
  /** True from `pagehide` until a back/forward-cache restore. */
  readonly unloading: boolean;
}

export function watchUnload(target: EventTarget): UnloadWatch {
  let unloading = false;
  target.addEventListener('pagehide', () => { unloading = true; });
  target.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) unloading = false;
  });
  return { get unloading() { return unloading; } };
}

/**
 * A `TypeError` while the page is going away is a fetch the browser cancelled.
 *
 * Matched on the type, not the text: each browser words it differently. A
 * `TypeError` from a code fault that happens to land inside the teardown is
 * skipped too; nothing on screen can break once the page is hidden.
 */
export function isCancelledByUnload(err: unknown, watch: UnloadWatch): boolean {
  return watch.unloading && err instanceof TypeError;
}

/** This page's watch, started when the module loads. */
export const pageUnload: UnloadWatch =
  typeof window !== 'undefined' ? watchUnload(window) : { unloading: false };
